import { ChangeDetectorRef, Component, Input } from '@angular/core';

import { combineLatest, Observable, Subject, take } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FeatureGuardFacade, GuardedFeature } from '@recruitee/feature-guard';
import { I18nService } from '@recruitee/i18n';
import type { JobSchedulerData } from '@recruitee/offers-types';
import { JobSchedulerButtonComponent } from '@recruitee/offers-ui';
import { BillingPlanTier } from '@recruitee/products-types';
import { IconToken } from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

import { JobSchedulerFacade } from '../../facades/job-scheduler.facade';
import { JobValidationErrors } from '../../types/job-validation-errors.types';

@Component({
  selector: 'rt-job-scheduler-button-container',
  template: `
    <rt-job-scheduler-button
      [schedulerData]="schedulerData"
      [active]="isActive && !disabled"
      [featureAvailable]="isFeatureAvailable"
      [discriminationIconColor]="featureDiscriminationIconColor"
      [discriminationTooltip]="featureDiscriminationTooltipText"
      (click)="buttonClick()"
    ></rt-job-scheduler-button>
  `,
  standalone: true,
  imports: [JobSchedulerButtonComponent],
})
export class JobSchedulerButtonContainerComponent {
  public readonly featureDiscrimination: { [key in string]: { icon: IconToken; tooltip: string } } =
    {
      [BillingPlanTier.Scale]: {
        icon: 'colorIconScaleDefault',
        tooltip: this.i18n.translate('app.pricing.feature_guard.available_in_scale'),
      },
      [BillingPlanTier.Grow]: {
        icon: 'colorIconGrowDefault',
        tooltip: this.i18n.translate('app.pricing.feature_guard.available_in_grow'),
      },
      [BillingPlanTier.Lead]: {
        icon: 'colorIconLeadDefault',
        tooltip: this.i18n.translate('app.pricing.feature_guard.available_in_lead'),
      },
      [BillingPlanTier.Optimize]: {
        icon: 'colorIconOptimizeDefault',
        tooltip: this.i18n.translate('app.pricing.feature_guard.available_in_optimize'),
      },
    };

  @Input() public disabled: boolean;
  @Input() public jobSlug: string;
  // FIXME
  //  after JobEditor logic is extracted to libs, inject JobEditorValidationService instead of having this Input
  @Input() public validate: () => Observable<JobValidationErrors>;
  @Input() public publish: (schedulerData: JobSchedulerData | null) => Observable<boolean>;

  public schedulerData: JobSchedulerData;
  public isActive: boolean;
  public isFeatureAvailable: boolean;
  public featureDiscriminationIconColor: IconToken = 'colorIconDefault';
  public featureDiscriminationTooltipText: string = '';

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private jobSchedulerFacade: JobSchedulerFacade,
    private featureGuardFacade: FeatureGuardFacade,
  ) {}

  public ngOnInit(): void {
    this.jobSchedulerFacade.schedulerData$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(data => {
      this.schedulerData = data;
      this.cdRef.detectChanges();
    });
    this.jobSchedulerFacade.schedulerActive$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(active => {
        this.isActive = active;
        this.cdRef.detectChanges();
      });
    combineLatest([
      this.featureGuardFacade.checkFeatureAvailability(GuardedFeature.JobScheduler),
      this.featureGuardFacade.getPlanWhereFeatureIsAvailable(GuardedFeature.JobScheduler),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([featureAvailable, availableOnPlan]) => {
        this.isFeatureAvailable = featureAvailable;
        if (!featureAvailable && availableOnPlan) {
          this.featureDiscriminationIconColor = this.featureDiscrimination[availableOnPlan].icon;
          this.featureDiscriminationTooltipText =
            this.featureDiscrimination[availableOnPlan].tooltip;
        }
      });
  }

  public buttonClick(): void {
    if (!this.isActive || this.disabled) return;
    if (this.isFeatureAvailable || this.schedulerData) {
      this.validate()
        .pipe(take(1))
        .subscribe(validationErrors => {
          this.jobSchedulerFacade.open({
            validationErrors,
            jobSlug: this.jobSlug,
            publish: (schedulerData: JobSchedulerData | null) => this.publish(schedulerData),
          });
        });
    } else {
      this.openFeatureGuardDialog();
    }
  }

  private openFeatureGuardDialog() {
    this.featureGuardFacade.showDialog(GuardedFeature.JobScheduler);
  }
}
